import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../../components/ui/Table';
import UserAssignmentPopover from './UserAssignmentPopover';
import Icon from '../../../../components/AppIcon';
import { ScheduleTemplate } from '../../../../types/scheduling';
import { appDate } from '../../../../utils/appDateFormat';
import { DateField, TimeField } from '../../../../components/dateFields';
import { parseApiDateOnly } from '@/utils/apiDate';
import { getGoverningRule } from '../governingRule';

// ─── Exported types ───

export interface EditRowData {
  name: string;
  description: string;
  isActive: boolean;
  days: string[];         // ['MO', 'TU', ...]
  interval: number;       // 1-4
  startTime: string;      // HH:mm
  endTime: string;        // HH:mm
  breakStart: string;     // HH:mm or ''
  breakEnd: string;       // HH:mm or ''
  breakDuration: number | null;
  userIds: string[];
  startDate: string;      // YYYY-MM-DD
  endDate: string;        // YYYY-MM-DD or '' (open-ended)
}

// ─── Exported helpers (used by page component in Task 8) ───

export function parseRRule(rRule: string): { days: string[]; interval: number } {
  const days = rRule.match(/BYDAY=([^;]+)/)?.[1]?.split(',') || [];
  const interval = parseInt(rRule.match(/INTERVAL=(\d+)/)?.[1] || '1', 10);
  return { days, interval };
}

export function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0');
  const m = (minutes % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

// ─── Constants ───

const ALL_DAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;

const SORTABLE_COLUMNS: Record<string, string> = {
  title: 'Title',
  status: 'Status',
  startTime: 'StartTime',
  interval: 'Interval',
};

// ─── Props ───

interface ScheduleTableProps {
  templates: ScheduleTemplate[];
  allUsers: { id: string; name: string; fullName?: string; department?: string }[];
  departments: string[];
  canManageSchedules: boolean;
  onEditStart: (templateId: number) => void;
  onArchive: (templateId: number) => void;
  // Edit mode props
  editingId: number | null;
  editData: EditRowData | null;
  onEditChange: (data: EditRowData) => void;
  onEditSave: () => void;
  onEditCancel: () => void;
  editSaving: boolean;
  // Creation props
  isCreating: boolean;
  createData: EditRowData | null;
  onCreateChange: (data: EditRowData) => void;
  onCreateSave: () => void;
  onCreateCancel: () => void;
  createSaving: boolean;
  // Sort props (Task 10 stub)
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  // Empty state
  totalCount: number;
  onClearFilters: () => void;
}

// ─── Component ───

const ScheduleTable: React.FC<ScheduleTableProps> = ({
  templates,
  allUsers,
  departments,
  canManageSchedules,
  onEditStart,
  onArchive,
  editingId,
  editData,
  onEditChange,
  onEditSave,
  onEditCancel,
  editSaving,
  isCreating,
  createData,
  onCreateChange,
  onCreateSave,
  onCreateCancel,
  createSaving,
  sortField,
  sortDir,
  onSort,
  totalCount,
  onClearFilters,
}) => {
  const { t } = useTranslation();
  const [showValidation, setShowValidation] = useState(false);
  const [showCreateValidation, setShowCreateValidation] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggleExpandedRow = (id: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Shared validation helper ──

  const computeValidationErrors = useCallback((data: EditRowData | null) => {
    if (!data) return {};
    const errors: Record<string, boolean> = {};
    if (!data.name.trim()) errors.name = true;
    if (data.days.length === 0) errors.days = true;
    if (!data.startTime) errors.startTime = true;
    if (!data.endTime) errors.endTime = true;
    if (data.userIds.length === 0) errors.userIds = true;
    return errors;
  }, []);

  // ── Edit validation ──

  const validationErrors = useMemo(
    () => computeValidationErrors(editData),
    [editData, computeValidationErrors],
  );

  // ── Create validation ──

  const createValidationErrors = useMemo(
    () => computeValidationErrors(createData),
    [createData, computeValidationErrors],
  );

  const handleEditConfirm = useCallback(() => {
    if (Object.keys(validationErrors).length > 0) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    onEditSave();
  }, [validationErrors, onEditSave]);

  const handleEditCancel = useCallback(() => {
    setShowValidation(false);
    onEditCancel();
  }, [onEditCancel]);

  const handleCreateConfirm = useCallback(() => {
    if (Object.keys(createValidationErrors).length > 0) {
      setShowCreateValidation(true);
      return;
    }
    setShowCreateValidation(false);
    onCreateSave();
  }, [createValidationErrors, onCreateSave]);

  const handleCreateCancel = useCallback(() => {
    setShowCreateValidation(false);
    onCreateCancel();
  }, [onCreateCancel]);

  const updateField = useCallback(
    <K extends keyof EditRowData>(field: K, value: EditRowData[K]) => {
      if (!editData) return;
      onEditChange({ ...editData, [field]: value });
    },
    [editData, onEditChange],
  );

  const updateCreateField = useCallback(
    <K extends keyof EditRowData>(field: K, value: EditRowData[K]) => {
      if (!createData) return;
      onCreateChange({ ...createData, [field]: value });
    },
    [createData, onCreateChange],
  );

  const toggleDay = useCallback(
    (day: string) => {
      if (!editData) return;
      const next = editData.days.includes(day)
        ? editData.days.filter((d) => d !== day)
        : [...editData.days, day];
      onEditChange({ ...editData, days: next });
    },
    [editData, onEditChange],
  );

  const toggleCreateDay = useCallback(
    (day: string) => {
      if (!createData) return;
      const next = createData.days.includes(day)
        ? createData.days.filter((d: string) => d !== day)
        : [...createData.days, day];
      onCreateChange({ ...createData, days: next });
    },
    [createData, onCreateChange],
  );

  // ── Shared editable row renderer ──

  const renderEditableRow = useCallback(
    (opts: {
      key: string | number;
      data: EditRowData;
      vErrors: Record<string, boolean>;
      colorHex: string;
      rowClassName: string;
      testIdPrefix: string;
      saving: boolean;
      onConfirm: () => void;
      onCancel: () => void;
      onUpdateField: <K extends keyof EditRowData>(field: K, value: EditRowData[K]) => void;
      onToggleDay: (day: string) => void;
    }) => {
      const {
        key,
        data,
        vErrors,
        colorHex,
        rowClassName,
        testIdPrefix,
        saving,
        onConfirm,
        onCancel,
        onUpdateField,
        onToggleDay,
      } = opts;

      const overnight =
        data.endTime && data.startTime && data.endTime <= data.startTime;

      return (
        <TableRow key={key} className={rowClassName} data-testid={testIdPrefix === 'create' ? 'create-row' : `schedule-row-${key}`}>
          {/* Title */}
          <TableCell style={{ minWidth: 180 }}>
            <input
              type="text"
              value={data.name}
              onChange={(e) => onUpdateField('name', e.target.value)}
              className={`w-full px-2 py-1 text-sm border rounded-md bg-background ${
                vErrors.name ? 'ring-1 ring-destructive border-destructive' : 'border-border'
              }`}
              data-testid={`${testIdPrefix}-name`}
            />
          </TableCell>

          {/* Status — read-only badge (status is computed from rule dates, not directly editable) */}
          <TableCell style={{ width: 90 }}>
            <span
              className={
                data.isActive
                  ? 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }
              data-testid={`${testIdPrefix}-status`}
            >
              {data.isActive
                ? t('Active', { defaultValue: 'Active' })
                : t('Inactive', { defaultValue: 'Inactive' })}
            </span>
          </TableCell>

          {/* Date Range */}
          <TableCell className="hidden md:table-cell" style={{ width: 130 }}>
            <div className="space-y-1">
              <DateField
                value={data.startDate}
                onChange={(v) => onUpdateField('startDate', v)}
                inputClassName="h-7 py-1 ps-1 text-xs"
                data-testid={`${testIdPrefix}-start-date`}
              />
              <DateField
                value={data.endDate}
                onChange={(v) => onUpdateField('endDate', v)}
                inputClassName="h-7 py-1 ps-1 text-xs"
                data-testid={`${testIdPrefix}-end-date`}
              />
            </div>
          </TableCell>

          {/* Days */}
          <TableCell style={{ width: 160 }}>
            <div
              className={`flex gap-0.5 flex-wrap ${
                vErrors.days ? 'ring-1 ring-destructive rounded p-0.5' : ''
              }`}
            >
              {ALL_DAYS.map((day) => {
                const isActiveDay = data.days.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => onToggleDay(day)}
                    className={
                      isActiveDay
                        ? 'bg-primary text-primary-foreground text-[10px] px-1 py-0.5 rounded font-medium cursor-pointer hover:opacity-80 transition-opacity'
                        : 'bg-card border border-border text-muted-foreground text-[10px] px-1 py-0.5 rounded cursor-pointer hover:bg-muted transition-colors'
                    }
                    data-testid={`${testIdPrefix}-day-${day}`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </TableCell>

          {/* Interval */}
          <TableCell className="hidden md:table-cell" style={{ width: 110 }}>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => onUpdateField('interval', n)}
                  className={
                    data.interval === n
                      ? 'bg-primary text-primary-foreground text-xs w-6 h-6 rounded font-medium cursor-pointer'
                      : 'bg-card border border-border text-muted-foreground text-xs w-6 h-6 rounded cursor-pointer hover:bg-muted transition-colors'
                  }
                  data-testid={`${testIdPrefix}-interval-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </TableCell>

          {/* Start Time */}
          <TableCell style={{ width: 80 }}>
            <TimeField
              value={data.startTime}
              onChange={(v) => onUpdateField('startTime', v)}
              inputClassName={`h-7 py-1 text-sm font-mono ${vErrors.startTime ? 'ring-1 ring-destructive border-destructive' : ''}`}
              data-testid={`${testIdPrefix}-start-time`}
            />
          </TableCell>

          {/* End Time + overnight */}
          <TableCell style={{ width: 80 }}>
            <div className="flex items-center gap-0.5">
              <TimeField
                value={data.endTime}
                onChange={(v) => onUpdateField('endTime', v)}
                inputClassName={`h-7 py-1 text-sm font-mono ${vErrors.endTime ? 'ring-1 ring-destructive border-destructive' : ''}`}
                data-testid={`${testIdPrefix}-end-time`}
              />
              {overnight && (
                <span
                  className="text-[10px] text-amber-500 dark:text-amber-400 flex-shrink-0"
                  title={t('OvernightShift', { defaultValue: 'Overnight shift' })}
                >
                  (+1)
                </span>
              )}
            </div>
          </TableCell>

          {/* Break Start */}
          <TableCell className="hidden lg:table-cell" style={{ width: 80 }}>
            <TimeField
              value={data.breakStart}
              onChange={(v) => onUpdateField('breakStart', v)}
              inputClassName="h-7 py-1 text-sm font-mono"
              data-testid={`${testIdPrefix}-break-start`}
            />
          </TableCell>

          {/* Break End */}
          <TableCell className="hidden lg:table-cell" style={{ width: 80 }}>
            <TimeField
              value={data.breakEnd}
              onChange={(v) => onUpdateField('breakEnd', v)}
              inputClassName="h-7 py-1 text-sm font-mono"
              data-testid={`${testIdPrefix}-break-end`}
            />
          </TableCell>

          {/* Break Duration */}
          <TableCell className="hidden md:table-cell" style={{ width: 80 }}>
            <input
              type="number"
              step={15}
              min={0}
              value={data.breakDuration ?? ''}
              onChange={(e) =>
                onUpdateField(
                  'breakDuration',
                  e.target.value === '' ? null : parseInt(e.target.value, 10),
                )
              }
              className="w-full px-1 py-1 text-sm font-mono border border-border rounded-md bg-background"
              data-testid={`${testIdPrefix}-break-duration`}
            />
          </TableCell>

          {/* Assigned Users */}
          <TableCell className="hidden md:table-cell" style={{ width: 140 }}>
            <div
              className={vErrors.userIds ? 'ring-1 ring-destructive rounded' : ''}
              data-testid={`${testIdPrefix}-users`}
            >
              <UserAssignmentPopover
                selectedUserIds={data.userIds}
                onChange={(ids) => onUpdateField('userIds', ids)}
                allUsers={allUsers}
                departments={departments}
                disabled={false}
              />
            </div>
          </TableCell>

          {/* Actions */}
          {canManageSchedules && (
            <TableCell style={{ width: 80 }}>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={saving}
                  className="p-1.5 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors text-green-600 dark:text-green-400 disabled:opacity-50"
                  title={t('Confirm', { defaultValue: 'Confirm' })}
                  data-testid={`${testIdPrefix}-confirm`}
                >
                  {saving ? (
                    <Icon name="Loader2" size={15} className="animate-spin" />
                  ) : (
                    <Icon name="Check" size={15} />
                  )}
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={saving}
                  className="p-1.5 rounded-md hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive disabled:opacity-50"
                  title={t('Cancel', { defaultValue: 'Cancel' })}
                  data-testid={`${testIdPrefix}-cancel`}
                >
                  <Icon name="X" size={15} />
                </button>
              </div>
            </TableCell>
          )}
        </TableRow>
      );
    },
    [t, allUsers, departments, canManageSchedules],
  );

  // ── Empty states (skip if creating — show table with creation row instead) ──

  if (totalCount === 0 && !isCreating) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="schedule-empty-state">
        <Icon name="Calendar" size={48} className="text-muted-foreground/40 mb-4" />
        <p className="text-muted-foreground text-sm mb-4">
          {t('NoSchedulesExist', { defaultValue: 'No recurring schedules yet. Create one to get started.' })}
        </p>
      </div>
    );
  }

  if (templates.length === 0 && totalCount > 0 && !isCreating) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="schedule-no-match-state">
        <Icon name="SearchX" size={48} className="text-muted-foreground/40 mb-4" />
        <p className="text-muted-foreground text-sm mb-2">
          {t('NoSchedulesMatchFilters', { defaultValue: 'No schedules match the current filters.' })}
        </p>
        <button
          type="button"
          onClick={onClearFilters}
          className="text-primary text-sm hover:underline"
          data-testid="schedule-clear-filters"
        >
          {t('ClearFilters', { defaultValue: 'Clear Filters' })}
        </button>
      </div>
    );
  }

  // ── Sort header helper ──

  const renderSortableHeader = (field: string, label: string) => {
    const isActive = sortField === field;
    return (
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 hover:text-foreground transition-colors select-none"
        data-testid={`sort-${field}`}
      >
        <span>{label}</span>
        {isActive && (
          <Icon
            name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'}
            size={14}
            className="text-foreground"
          />
        )}
      </button>
    );
  };

  return (
    <div className="overflow-x-auto">
    <Table stickyHeader>
      <TableHeader>
        <TableRow>
          <TableHead style={{ minWidth: 180 }}>
            {renderSortableHeader('title', t('Title', { defaultValue: 'Title' }))}
          </TableHead>
          <TableHead style={{ width: 90 }}>
            {renderSortableHeader('status', t('Status', { defaultValue: 'Status' }))}
          </TableHead>
          <TableHead className="hidden md:table-cell" style={{ width: 130 }}>
            {t('DateRange', { defaultValue: 'Date Range' })}
          </TableHead>
          <TableHead style={{ width: 160 }}>
            {t('Days', { defaultValue: 'Days' })}
          </TableHead>
          <TableHead className="hidden md:table-cell" style={{ width: 110 }}>
            {renderSortableHeader('interval', t('Interval', { defaultValue: 'Interval' }))}
          </TableHead>
          <TableHead style={{ width: 80 }}>
            {renderSortableHeader('startTime', t('ShiftTime', { defaultValue: 'Shift Time' }))}
          </TableHead>
          <TableHead className="hidden md:table-cell" style={{ width: 80 }}>
            {t('EndTime', { defaultValue: 'End Time' })}
          </TableHead>
          <TableHead className="hidden lg:table-cell" style={{ width: 80 }}>
            {t('BreakStart', { defaultValue: 'Break Start' })}
          </TableHead>
          <TableHead className="hidden lg:table-cell" style={{ width: 80 }}>
            {t('BreakEnd', { defaultValue: 'Break End' })}
          </TableHead>
          <TableHead className="hidden md:table-cell" style={{ width: 80 }}>
            {t('BreakDuration', { defaultValue: 'Break Dur.' })}
          </TableHead>
          <TableHead className="hidden md:table-cell" style={{ width: 140 }}>
            {t('AssignedUsers', { defaultValue: 'Assigned Users' })}
          </TableHead>
          {canManageSchedules && (
            <TableHead className="hidden md:table-cell" style={{ width: 80 }}>
              {t('Actions', { defaultValue: 'Actions' })}
            </TableHead>
          )}
          <TableHead className="md:hidden w-10"></TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {/* ── Creation row (always first) ── */}
        {isCreating && createData && renderEditableRow({
          key: 'create-new',
          data: createData,
          vErrors: showCreateValidation ? createValidationErrors : {},
          colorHex: '#6366f1',
          rowClassName: 'bg-green-50/50 ring-1 ring-green-200/50 dark:bg-green-900/10 dark:ring-green-800/30',
          testIdPrefix: 'create',
          saving: createSaving,
          onConfirm: handleCreateConfirm,
          onCancel: handleCreateCancel,
          onUpdateField: updateCreateField,
          onToggleDay: toggleCreateDay,
        })}

        {templates.map((template) => {
          const rule = getGoverningRule(template.recurringRules);
          if (!rule) return null;

          const { days: activeDays, interval } = parseRRule(rule.rRule);
          const startTime = minutesToTime(rule.startTimeOfDayMinutes);
          const endTime = minutesToTime(rule.endTimeOfDayMinutes);
          const isOvernight = rule.endTimeOfDayMinutes <= rule.startTimeOfDayMinutes;

          const breakStart =
            rule.breakStartTimeOfDayMinutes != null
              ? minutesToTime(rule.breakStartTimeOfDayMinutes)
              : null;
          const breakEnd =
            rule.breakEndTimeOfDayMinutes != null
              ? minutesToTime(rule.breakEndTimeOfDayMinutes)
              : null;
          const breakDuration =
            rule.breakDurationMinutes != null ? `${rule.breakDurationMinutes}m` : null;

          const isActive = template.status === 'Active';
          const isEditing = editingId === template.id && editData !== null;

          // ── Editing row ──
          if (isEditing) {
            return renderEditableRow({
              key: template.id,
              data: editData,
              vErrors: showValidation ? validationErrors : {},
              colorHex: template.color || '#6366f1',
              rowClassName: 'bg-primary/5 ring-1 ring-primary/20',
              testIdPrefix: 'edit',
              saving: editSaving,
              onConfirm: handleEditConfirm,
              onCancel: handleEditCancel,
              onUpdateField: updateField,
              onToggleDay: toggleDay,
            });
          }

          // ── Compute derived values for expanded row ──
          const intervalLabel = t('EveryNWeeks', {
            count: interval,
            defaultValue: `Every ${interval} week(s)`,
          });
          const breakLabel = breakDuration
            ? breakDuration
            : breakStart && breakEnd
              ? `${breakStart} - ${breakEnd}`
              : '\u2014';
          const userCount = (template.assignedUserIds || []).length;

          // ── Read-only row ──
          return (
            <React.Fragment key={template.id}>
            <TableRow data-testid={`schedule-row-${template.id}`}>
              {/* Title */}
              <TableCell style={{ minWidth: 180 }}>
                <span className="truncate block max-w-[180px]" title={template.name}>
                  {template.name}
                </span>
              </TableCell>

              {/* Status badge */}
              <TableCell style={{ width: 90 }}>
                <span
                  className={
                    isActive
                      ? 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  }
                >
                  {isActive
                    ? t('Active', { defaultValue: 'Active' })
                    : t('Inactive', { defaultValue: 'Inactive' })}
                </span>
              </TableCell>

              {/* Date Range */}
              <TableCell className="hidden md:table-cell" style={{ width: 130 }}>
                <div className="text-xs text-muted-foreground leading-relaxed">
                  <div>{rule.startDateUtc ? appDate(parseApiDateOnly(rule.startDateUtc)) : '\u2014'}</div>
                  <div>{rule.endDateUtc && !rule.endDateUtc.startsWith('9999')
                    ? appDate(parseApiDateOnly(rule.endDateUtc))
                    : t('OpenEnded', { defaultValue: 'Open-ended' })}</div>
                </div>
              </TableCell>

              {/* Day chips */}
              <TableCell style={{ width: 160 }}>
                <div className="flex gap-0.5 flex-wrap">
                  {ALL_DAYS.map((day) => {
                    const isActiveDay = activeDays.includes(day);
                    return (
                      <span
                        key={day}
                        className={
                          isActiveDay
                            ? 'bg-primary text-primary-foreground text-[10px] px-1 py-0.5 rounded font-medium'
                            : 'bg-muted text-muted-foreground/30 text-[10px] px-1 py-0.5 rounded'
                        }
                      >
                        {day}
                      </span>
                    );
                  })}
                </div>
              </TableCell>

              {/* Interval */}
              <TableCell className="hidden md:table-cell" style={{ width: 110 }}>
                <span className="text-xs text-muted-foreground">
                  {intervalLabel}
                </span>
              </TableCell>

              {/* Start Time (on mobile shows combined start-end) */}
              <TableCell style={{ width: 80 }}>
                <span className="text-sm font-mono">{startTime}</span>
                <span className="md:hidden text-sm font-mono text-muted-foreground"> - {endTime}</span>
                {isOvernight && (
                  <span
                    className="md:hidden ms-1 text-[10px] text-amber-500 dark:text-amber-400"
                    title={t('OvernightShift', { defaultValue: 'Overnight shift' })}
                  >
                    (+1)
                  </span>
                )}
              </TableCell>

              {/* End Time + overnight indicator (hidden on mobile) */}
              <TableCell className="hidden md:table-cell" style={{ width: 80 }}>
                <span className="text-sm font-mono">{endTime}</span>
                {isOvernight && (
                  <span
                    className="ms-1 text-[10px] text-amber-500 dark:text-amber-400"
                    title={t('OvernightShift', { defaultValue: 'Overnight shift' })}
                  >
                    (+1)
                  </span>
                )}
              </TableCell>

              {/* Break Start */}
              <TableCell className="hidden lg:table-cell" style={{ width: 80 }}>
                <span className="text-sm font-mono text-muted-foreground">
                  {breakStart || '\u2014'}
                </span>
              </TableCell>

              {/* Break End */}
              <TableCell className="hidden lg:table-cell" style={{ width: 80 }}>
                <span className="text-sm font-mono text-muted-foreground">
                  {breakEnd || '\u2014'}
                </span>
              </TableCell>

              {/* Break Duration */}
              <TableCell className="hidden md:table-cell" style={{ width: 80 }}>
                <span className="text-sm font-mono text-muted-foreground">
                  {breakDuration || '\u2014'}
                </span>
              </TableCell>

              {/* Assigned Users */}
              <TableCell className="hidden md:table-cell" style={{ width: 140 }}>
                <UserAssignmentPopover
                  selectedUserIds={template.assignedUserIds || []}
                  onChange={() => {}}
                  allUsers={allUsers}
                  departments={departments}
                  disabled
                />
              </TableCell>

              {/* Actions */}
              {canManageSchedules && (
                <TableCell className="hidden md:table-cell" style={{ width: 80 }}>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onEditStart(template.id)}
                      className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                      title={t('Edit', { defaultValue: 'Edit' })}
                      data-testid={`schedule-edit-${template.id}`}
                    >
                      <Icon name="Pencil" size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onArchive(template.id)}
                      className="p-1.5 rounded-md hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive"
                      title={t('Archive', { defaultValue: 'Archive' })}
                      data-testid={`schedule-archive-${template.id}`}
                    >
                      <Icon name="Archive" size={15} />
                    </button>
                  </div>
                </TableCell>
              )}

              {/* Mobile expand chevron */}
              <TableCell className="md:hidden">
                <button
                  onClick={() => toggleExpandedRow(template.id)}
                  data-testid={`schedule-expand-${template.id}`}
                  className="p-1 rounded hover:bg-muted"
                >
                  <Icon name={expandedRows.has(template.id) ? 'ChevronUp' : 'ChevronDown'} size={16} />
                </button>
              </TableCell>
            </TableRow>

            {/* Mobile expanded detail row */}
            {expandedRows.has(template.id) && (
              <TableRow className="md:hidden bg-muted/30" data-testid={`schedule-expanded-${template.id}`}>
                <TableCell colSpan={99} className="p-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">{t('DateRange', { defaultValue: 'Date Range' })}:</span> {rule.startDateUtc ? appDate(parseApiDateOnly(rule.startDateUtc)) : '\u2014'} — {rule.endDateUtc && !rule.endDateUtc.startsWith('9999') ? appDate(parseApiDateOnly(rule.endDateUtc)) : t('OpenEnded', { defaultValue: 'Open-ended' })}</div>
                    <div><span className="text-muted-foreground">{t('Interval')}:</span> {intervalLabel}</div>
                    <div><span className="text-muted-foreground">{t('Break')}:</span> {breakLabel}</div>
                    <div><span className="text-muted-foreground">{t('AssignedEmployees', { defaultValue: 'Assigned Employees' })}:</span> {userCount}</div>
                    {canManageSchedules && (
                      <div className="col-span-2 flex gap-2 pt-1">
                        <button onClick={() => onEditStart(template.id)} className="text-sm text-primary hover:underline" data-testid={`schedule-expand-edit-${template.id}`}>{t('Edit')}</button>
                        <button onClick={() => onArchive(template.id)} className="text-sm text-destructive hover:underline" data-testid={`schedule-expand-archive-${template.id}`}>{t('Archive')}</button>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
    </div>
  );
};

export default ScheduleTable;
